import React from 'react';

import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';

import { CloudDisplay } from 'shared/base';

import { AssetResourceCategoryMenuItem } from '../../../../types';
import { countFormat } from '../../../../utils/table';

import { groupSubitemsByCloud } from '../utils/groupSubitems';

interface SubcategoryListProps {
  categoryIcon: React.ReactNode;
  categoryValue: string;
  isExpanded: boolean;
  menuItem: AssetResourceCategoryMenuItem;
  onCategoryClick: (value: string) => void;
  onTypeAndCategoryClick: (type: string) => void;
  resourceCategory: string | undefined;
  resourceType: string | undefined;
}

export function SubcategoryList({
  categoryIcon,
  categoryValue,
  isExpanded,
  menuItem,
  onCategoryClick,
  onTypeAndCategoryClick,
  resourceCategory,
  resourceType,
}: SubcategoryListProps) {
  const { count, label, subitems } = menuItem;
  const isSelected = categoryValue === resourceCategory || (!resourceCategory && !categoryValue);
  const groupedSubitems = groupSubitemsByCloud(subitems);

  const handleCategoryClick = () => {
    onCategoryClick(categoryValue);
  };

  const handleTypeClick = (type: string) => {
    onTypeAndCategoryClick(type);
  };

  return (
    <Collapse in={isExpanded} timeout="auto" unmountOnExit={true}>
      <Box>
        <List component="div" data-testid="resource-type-list" dense={true} sx={{ py: 0 }}>
          <ListItemButton
            onClick={handleCategoryClick}
            selected={isSelected && !resourceType}
            sx={{ pl: 5.5 }}
          >
            <ListItemIcon>{categoryIcon}</ListItemIcon>
            <ListItemText
              primary={label == 'All data' ? label : `All ${label}`}
              secondary={countFormat(count)}
              sx={{ display: 'inline-flex', justifyContent: 'space-between', my: '5px' }}
            />
          </ListItemButton>

          {groupedSubitems.map(([cloud, cloudSubitems]) => (
            <React.Fragment key={cloud}>
              {cloudSubitems?.map((subitem) => (
                <ListItemButton
                  key={`${categoryValue}-${subitem.value}`}
                  onClick={() => handleTypeClick(subitem.value)}
                  selected={subitem.value === resourceType}
                  sx={{ pl: 5.5 }}
                >
                  <ListItemIcon>
                    <CloudDisplay
                      grayscale={subitem.value !== resourceType}
                      size={18}
                      value={cloud}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={subitem.label}
                    secondary={countFormat(subitem.count)}
                    sx={{ display: 'inline-flex', justifyContent: 'space-between', my: '5px' }}
                  />
                </ListItemButton>
              ))}
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Collapse>
  );
}
